import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatTime24 } from "@/panes/automation/cadence";
import {
  supportedTimeZones,
  type ScheduleDraft,
  type ScheduleDraftType,
} from "@/panes/automation/scheduleDraft";
import { cn } from "@/utils";
import { Check, ChevronDown, Clock, Globe } from "lucide-react";
import { useMemo, useState } from "react";

const CADENCE_OPTIONS: { value: ScheduleDraftType; label: string }[] = [
  { value: "none", label: "No schedule" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "annually", label: "Yearly" },
];

const WEEKDAYS: { value: string; label: string; full: string }[] = [
  { value: "0", label: "S", full: "Sunday" },
  { value: "1", label: "M", full: "Monday" },
  { value: "2", label: "T", full: "Tuesday" },
  { value: "3", label: "W", full: "Wednesday" },
  { value: "4", label: "T", full: "Thursday" },
  { value: "5", label: "F", full: "Friday" },
  { value: "6", label: "S", full: "Saturday" },
];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function ordinal(n: number): string {
  const rem10 = n % 10;
  const rem100 = n % 100;
  if (rem10 === 1 && rem100 !== 11) return `${n}st`;
  if (rem10 === 2 && rem100 !== 12) return `${n}nd`;
  if (rem10 === 3 && rem100 !== 13) return `${n}rd`;
  return `${n}th`;
}

/** 96 fifteen-minute slots, "HH:mm". */
const TIME_OPTIONS: string[] = Array.from({ length: 96 }, (_, i) => {
  const hours = Math.floor(i / 4);
  const minutes = (i % 4) * 15;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
});

const PICKER_TRIGGER_CLASS =
  "flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm transition-colors hover:bg-n-05 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px] disabled:cursor-not-allowed disabled:opacity-50";

function TimePickerField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (time24: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={PICKER_TRIGGER_CLASS}
          aria-label="Time"
        >
          <Clock className="size-3.5 shrink-0 text-n-50" />
          <span className="flex-1 text-left">{formatTime24(value)}</span>
          <ChevronDown className="size-3.5 shrink-0 text-n-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-40 p-0">
        <Command>
          <CommandInput placeholder="Time" />
          <CommandList>
            <CommandEmpty>No matching time.</CommandEmpty>
            <CommandGroup>
              {TIME_OPTIONS.map((time) => (
                <CommandItem
                  key={time}
                  value={formatTime24(time)}
                  onSelect={() => {
                    onChange(time);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "size-3.5",
                      time === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {formatTime24(time)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function TimeZoneField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (timeZone: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const timeZones = useMemo(supportedTimeZones, []);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={PICKER_TRIGGER_CLASS}
          aria-label="Time zone"
        >
          <Globe className="size-3.5 shrink-0 text-n-50" />
          <span className="min-w-0 flex-1 truncate text-left">{value}</span>
          <ChevronDown className="size-3.5 shrink-0 text-n-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <Command>
          <CommandInput placeholder="Search time zones" />
          <CommandList>
            <CommandEmpty>No matching time zone.</CommandEmpty>
            <CommandGroup>
              {timeZones.map((timeZone) => (
                <CommandItem
                  key={timeZone}
                  value={timeZone}
                  onSelect={() => {
                    onChange(timeZone);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "size-3.5",
                      timeZone === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{timeZone}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function DaysOfMonthField({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (days: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const sorted = [...value].map(Number).sort((a, b) => a - b);
  const label =
    sorted.length === 0
      ? "Pick days"
      : `On the ${sorted.map(ordinal).join(", ")}`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={PICKER_TRIGGER_CLASS}
          aria-label="Days of the month"
        >
          <span className="min-w-0 flex-1 truncate text-left">{label}</span>
          <ChevronDown className="size-3.5 shrink-0 text-n-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-48 p-0">
        <Command>
          <CommandList>
            <CommandGroup>
              {Array.from({ length: 31 }, (_, i) => String(i + 1)).map(
                (day) => {
                  const checked = value.includes(day);
                  return (
                    <CommandItem
                      key={day}
                      value={day}
                      onSelect={() => {
                        // Keep at least one day selected — an empty set has
                        // no cron representation.
                        if (checked && value.length === 1) return;
                        onChange(
                          checked
                            ? value.filter((d) => d !== day)
                            : [...value, day],
                        );
                      }}
                    >
                      <Check
                        className={cn(
                          "size-3.5",
                          checked ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {ordinal(Number(day))}
                    </CommandItem>
                  );
                },
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The structured schedule controls — a pure controlled component shared by
 * the pane editor (commit-on-change) and the create form (staged). Fields
 * appear by cadence, mirroring the retired ScheduleFormFields shape: weekly
 * gets day toggles, monthly gets a day multi-pick, yearly gets month + day.
 */
export function AutomationScheduleFields({
  value,
  onChange,
  disabled,
}: {
  value: ScheduleDraft;
  onChange: (draft: ScheduleDraft) => void;
  disabled?: boolean;
}) {
  const showTime = value.scheduleType !== "none";

  return (
    <div className="flex flex-col gap-2">
      <Select
        value={value.scheduleType}
        onValueChange={(scheduleType) =>
          onChange({
            ...value,
            scheduleType: scheduleType as ScheduleDraftType,
          })
        }
        disabled={disabled}
      >
        <SelectTrigger className="h-9" aria-label="Cadence">
          <SelectValue placeholder="Cadence" />
        </SelectTrigger>
        <SelectContent>
          {CADENCE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {value.scheduleType === "weekly" && (
        <div
          className="flex items-center gap-1"
          role="group"
          aria-label="Days of the week"
        >
          {WEEKDAYS.map((day) => {
            const pressed = value.daysOfWeek.includes(day.value);
            return (
              <button
                key={day.value}
                type="button"
                disabled={disabled}
                aria-pressed={pressed}
                aria-label={day.full}
                title={day.full}
                onClick={() => {
                  // Keep at least one day — an empty weekly set silently
                  // becomes Sunday in the cron layer.
                  if (pressed && value.daysOfWeek.length === 1) return;
                  onChange({
                    ...value,
                    daysOfWeek: pressed
                      ? value.daysOfWeek.filter((d) => d !== day.value)
                      : [...value.daysOfWeek, day.value],
                  });
                }}
                // Control mode is a state, not a held object: pressed rides
                // the neutral register (n-10 fill + n-90 ink), never accent.
                className={cn(
                  "flex size-7 items-center justify-center rounded-md border text-xs font-medium transition-colors",
                  pressed
                    ? "border-transparent bg-n-10 text-n-90"
                    : "text-n-50 hover:bg-n-05 hover:text-n-70",
                  disabled && "pointer-events-none opacity-50",
                )}
              >
                {day.label}
              </button>
            );
          })}
        </div>
      )}

      {value.scheduleType === "monthly" && (
        <DaysOfMonthField
          value={value.daysOfMonth}
          onChange={(daysOfMonth) => onChange({ ...value, daysOfMonth })}
          disabled={disabled}
        />
      )}

      {value.scheduleType === "annually" && (
        <div className="flex gap-2">
          <Select
            value={value.month}
            onValueChange={(month) => onChange({ ...value, month })}
            disabled={disabled}
          >
            <SelectTrigger className="h-9 flex-1" aria-label="Month">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((monthName, index) => (
                <SelectItem key={monthName} value={String(index + 1)}>
                  {monthName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={value.daysOfMonth[0] ?? "1"}
            onValueChange={(day) => onChange({ ...value, daysOfMonth: [day] })}
            disabled={disabled}
          >
            <SelectTrigger className="h-9 w-24" aria-label="Day">
              <SelectValue placeholder="Day" />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 31 }, (_, i) => String(i + 1)).map(
                (day) => (
                  <SelectItem key={day} value={day}>
                    {ordinal(Number(day))}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {showTime && (
        <div className="flex gap-2">
          <div className="w-32 shrink-0">
            <TimePickerField
              value={value.time24}
              onChange={(time24) => onChange({ ...value, time24 })}
              disabled={disabled}
            />
          </div>
          <div className="min-w-0 flex-1">
            <TimeZoneField
              value={value.timeZone}
              onChange={(timeZone) => onChange({ ...value, timeZone })}
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  );
}
